import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';
import SeoBreadcrumbs from '@/components/SeoBreadcrumbs';
import { ArrowRight, CheckCircle, Clock, FileText, Image } from 'lucide-react';

const PropertyMarketingFloorPlans = () => {
  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Property Marketing Floor Plans", path: "/property-marketing-floor-plans" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        pageId="property-marketing-floor-plans"
        defaultTitle="Property Marketing Floor Plans | House Plan Direct"
        defaultDescription="Professional floor plans for property marketing and brochures. Perfect for estate agents, developers, and property photographers. From £40, 24-48 hour delivery."
        aiSummary="House Plan Direct creates professional floor plans for property marketing across the UK. Our floor plans are perfect for property brochures, marketing materials, and digital listings. Used by estate agents, developers, and property photographers. Plans from £40."
        keywords="property marketing floor plans, property brochure floor plan, floor plan for property brochure, marketing floor plans UK, professional property floor plan"
      />
      <SeoBreadcrumbs items={breadcrumbs} />
      <Header />
      
      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-orange-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center mb-4">
            <Image className="h-12 w-12 text-orange-600" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Property Marketing Floor Plans
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Professional floor plans for brochures, marketing materials, and property presentations.
          </p>
          <Link to="/order">
            <Button size="lg" className="bg-orange-600 hover:bg-orange-700">
              Order Floor Plan <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
          <p className="text-sm text-gray-500 mt-4">
            Prefer to do it yourself? Use the free <Link to="/floor-plan-creator" className="text-orange-600 underline hover:text-orange-700" data-testid="marketing-link-floor-plan-creator">Floor Plan Creator</Link> to draw and present your own plan.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Floor Plans for Property Brochures</h2>
          <p className="text-gray-600 mb-8">
            High-quality floor plans are essential for professional property marketing. Whether you're creating 
            property brochures, sales particulars, or digital marketing materials, our floor plans provide the 
            professional presentation your properties deserve.
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-orange-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Use Cases</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-orange-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Property brochures & particulars</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-orange-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Sales & marketing presentations</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-orange-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Digital marketing campaigns</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-orange-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">Property development marketing</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Formats Included</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <FileText className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">JPEG - Perfect for digital use</span>
                </li>
                <li className="flex items-start">
                  <FileText className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">PDF - High quality for print</span>
                </li>
                <li className="flex items-start">
                  <FileText className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">DWG - Editable CAD format</span>
                </li>
              </ul>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-orange-600 text-white rounded-xl p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Get Your Marketing Floor Plan</h3>
            <p className="text-orange-100 mb-6">Professional floor plans delivered within 24-48 hours.</p>
            <Link to="/order">
              <Button size="lg" variant="secondary" className="bg-white text-orange-600 hover:bg-gray-100">
                Order Now <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PropertyMarketingFloorPlans;
